/**
 * The agent's reasoning, while it reasons.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY IT IS COLLAPSED THE MOMENT THE ANSWER ARRIVES
 *
 * Watching a model think is reassuring during the wait and noise afterwards.
 * The reasoning on a real investigation runs to several hundred fragments —
 * longer than the answer it produced — so leaving it open buries the one part
 * the customer came for.
 *
 * So it is open and streaming while the turn runs, and folds to a single line
 * the instant there is an answer to read. Still there, one click away, for the
 * person who wants to check the working.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY IT IS TYPOGRAPHICALLY QUIETER THAN THE ANSWER
 *
 * This is a SUMMARY of reasoning, not a conclusion, and some of it is the model
 * considering things it then rejects. Rendering it in the same weight as the
 * answer invites somebody to read a discarded number as a finding. Smaller,
 * dimmer, indented behind a rule — the visual grammar of a margin note, which
 * is what it is.
 */

import { useEffect, useRef, useState } from 'react';
import { Brain, ChevronRight } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import { cn } from '@/lib/utils';

export function Thinking({ text, live }: { text: string; live: boolean }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  /*
    Follows the newest reasoning while it streams, but only the INNER box —
    scrolling the page for a margin note would drag the answer out of view.
  */
  useEffect(() => {
    if (live && boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [text, live]);

  if (!text.trim()) return null;
  const expanded = live || open;

  return (
    <div className="mb-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={live}
        className={cn(
          'flex items-center gap-1.5 text-[11.5px] text-muted-foreground',
          !live && 'hover:text-foreground',
        )}
      >
        <Brain className={cn('size-3', live && 'animate-pulse')} aria-hidden />
        <span>{live ? t('thinking_live') : t('thinking_done')}</span>
        {live ? null : (
          <ChevronRight
            className={cn('size-3 transition-transform rtl:rotate-180', open && 'rotate-90 rtl:rotate-90')}
            aria-hidden
          />
        )}
      </button>

      {expanded ? (
        <div
          ref={boxRef}
          dir="auto"
          className={cn(
            'mt-1.5 border-s border-border ps-3',
            'text-[11.5px] leading-relaxed whitespace-pre-wrap text-muted-foreground',
            // Capped while live so a long chain cannot push the composer off
            // the screen; uncapped once the customer has chosen to read it.
            live && 'max-h-40 overflow-y-auto',
          )}
        >
          {text}
        </div>
      ) : null}
    </div>
  );
}
